import { Button, Flex, Group, Pagination, Stack, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useTableState } from '@/components/ui-shared/Tables/useTableState'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetWorkspaceApiKeys } from '@/queries/workspaceQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { ApiKeysList } from './ApiKeysList/ApiKeysList'
import { CreateApiKeyModal } from './ApiKeysList/CreateApiKeyModal'
import { useWorkspace } from './WorkspaceContext'

const PAGE_SIZE = 10

export const WorkspaceApiKeysPage = () => {
  const { workspace } = useWorkspace()

  const [
    isCreateModalOpened,
    { open: openCreateModal, close: closeCreateModal }
  ] = useDisclosure(false)

  const [tableState, setTableState] = useTableState({
    activePage: 1
  })

  const { activePage } = tableState

  const {
    data: apiKeysData,
    isFetching,
    isLoading,
    isError,
    refetch
  } = useGetWorkspaceApiKeys(
    workspace.id,
    {
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE
    },
    {
      keepPreviousData: true
    }
  )

  const totalPageCount = getPageCount(apiKeysData?.count || 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const handleApiKeyCreated = () => {
    setTableState({ activePage: 1 })
  }

  const [canCreateApiKey] = useHasPermissions(Resource.WorkspaceApiKeys, [
    CrudAction.Create
  ])

  if (!canCreateApiKey) {
    return (
      <Stack gap="xs">
        <Title order={3}>
          <FormattedMessage id="workspace.tabs.apiKeys" />
        </Title>

        <PlatformAlert variant="warning">
          <FormattedMessage id="workspace.apiKeys.noPermission" />
        </PlatformAlert>
      </Stack>
    )
  }

  return (
    <>
      <Stack gap="xs">
        <Group justify="space-between" gap="sm">
          <Title order={3}>
            <FormattedMessage id="workspace.tabs.apiKeys" />
          </Title>

          <Button
            leftSection={<IconPlus size={16} />}
            size="sm"
            onClick={openCreateModal}
          >
            <FormattedMessage id="workspace.apiKeys.create" />
          </Button>
        </Group>

        <ApiKeysList
          apiKeys={apiKeysData?.results || []}
          isLoading={isLoading || isFetching}
          hasError={isError}
          onReload={() => void refetch()}
        />

        <Flex justify="end" mt="lg">
          <Pagination
            size="sm"
            total={totalPageCount}
            value={activePage}
            onChange={(value) => setTableState({ activePage: value })}
          />
        </Flex>
      </Stack>

      <CreateApiKeyModal
        opened={isCreateModalOpened}
        onClose={closeCreateModal}
        onSuccess={handleApiKeyCreated}
      />
    </>
  )
}
